export interface RecoverableMessage {
  role: 'user' | 'assistant';
  transcript: string;
  failed?: boolean;
  clientSendId?: string;
}

export const FAILED_TURN_TEXT = 'La réponse a échoué. Reformuler la question pour réessayer.';

export function textOfStoredTurn(text: string, failed?: boolean): string {
  return failed && !text.trim() ? FAILED_TURN_TEXT : text;
}

export const newClientSendId = (): string => crypto.randomUUID();

export type RetryPlan = 'resend' | 'already-processed';

/* Le serveur ne persiste le tour (question + réponse) qu'à la fin : si, après
   une coupure, la question figure déjà côté serveur au-delà des messages connus
   avant l'envoi, le tour a abouti — renvoyer le dupliquerait (et le facturerait).
   Sinon rien n'a été enregistré : renvoyer est sûr. Un tour persisté uniquement
   à cause d'une panne (réponse assistant marquée `failed`, immédiatement après la dernière
   occurrence de la question) n'a pas abouti : on renvoie.
   Le serveur ne renvoie que les `windowCap` messages les plus récents : une fois la fenêtre
   pleine, un tour abouti la décale et l'index `knownBefore` ne s'aligne plus ; on ne regarde
   alors que le dernier tour (question + réponse).
   Quand l'envoi porte un `clientSendId`, un message serveur qui en porte un se compare par
   identifiant, jamais par texte (« oui », « continue » répétés) ; un message sans identifiant
   (antérieur à NAI-385) retombe sur la comparaison du texte. */
export async function planRetry(
  loadServerMessages: () => Promise<RecoverableMessage[]>,
  knownBefore: number,
  text: string,
  windowCap = Number.POSITIVE_INFINITY,
  clientSendId?: string,
): Promise<RetryPlan> {
  try {
    const messages = await loadServerMessages();
    const saturated = messages.length >= windowCap;
    const fresh = messages.slice(saturated ? Math.min(knownBefore, messages.length - 2) : knownBefore);
    const isSend = (m: RecoverableMessage) =>
      m.role === 'user' &&
      (clientSendId && m.clientSendId ? m.clientSendId === clientSendId : m.transcript.trim() === text.trim());
    const at = fresh.map(isSend).lastIndexOf(true);
    if (at < 0) return 'resend';
    const reply = fresh[at + 1];
    return reply?.role === 'assistant' && reply.failed ? 'resend' : 'already-processed';
  } catch {
    return 'resend';
  }
}
